import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { useLanguage } from '../../contexts/LanguageContext';
import { ensureDirectConversation, resolveShareRecipientDirectMeta } from '../../services/directChatService';
import DirectMessagePanel from './DirectMessagePanel';
import DealConversationDealLink from './DealConversationDealLink';

/**
 * @param {{
 *   onClose: () => void,
 *   conversationId: string,
 *   currentUser: { uid: string, displayName?: string, email?: string } | null,
 *   title?: string,
 *   subtitle?: string,
 *   ensureSpec?: { participantUids: string[], kind: string, context: Record<string, string> } | null,
 *   directConvMeta?: { kind?: string, context?: Record<string, unknown> } | null,
 *   currentStep?: string,
 *   currentProperty?: { id?: string } | null,
 *   onOpenDealFromChat?: (payload: { type: 'owned', propertyId: string } | { type: 'shared', shareToken: string }) => void,
 * }} props
 */
export default function DirectChatModal({
  onClose,
  conversationId,
  currentUser,
  title,
  subtitle,
  ensureSpec = null,
  directConvMeta = null,
  currentStep = '',
  currentProperty = null,
  onOpenDealFromChat,
}) {
  const { t } = useLanguage();
  const mergedDirectMeta = useMemo(
    () => resolveShareRecipientDirectMeta(conversationId, directConvMeta, ensureSpec),
    [conversationId, directConvMeta, ensureSpec],
  );
  const [ensureErrorKind, setEnsureErrorKind] = useState(null);
  const [ensuring, setEnsuring] = useState(!!ensureSpec);

  useEffect(() => {
    if (!ensureSpec || !conversationId) {
      setEnsuring(false);
      return undefined;
    }
    let cancelled = false;
    setEnsureErrorKind(null);
    setEnsuring(true);
    ensureDirectConversation(conversationId, ensureSpec)
      .catch((e) => {
        if (!cancelled) {
          console.warn('[directChat] ensureDirectConversation failed', conversationId, e?.code, e?.message);
          setEnsureErrorKind(e?.code === 'DIRECT_CHAT_BROKER_CLIENT' ? 'brokerClient' : 'generic');
        }
      })
      .finally(() => {
        if (!cancelled) setEnsuring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, ensureSpec]);

  useRegisterTopEscapeClose(onClose);

  return (
    <div
      className="fixed inset-0 z-[240] rentalyzer-modal-overlay rentalyzer-modal-overlay-stacked flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="direct-chat-modal-title"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col border border-slate-200">
        <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="min-w-0 flex-1 pr-2">
            <h2 id="direct-chat-modal-title" className="text-lg font-semibold text-slate-900 truncate">
              {title || t('directChat.modalTitle')}
            </h2>
            {subtitle ? <p className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</p> : null}
            <DealConversationDealLink
              conversationId={conversationId}
              directConvMeta={mergedDirectMeta}
              currentUser={currentUser}
              currentStep={currentStep}
              currentProperty={currentProperty}
              onOpenDealFromChat={onOpenDealFromChat}
              t={t}
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 shrink-0"
            aria-label={t('cancel')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {ensuring ? (
            <p className="text-sm text-slate-500 py-6 text-center">{t('loading')}</p>
          ) : ensureErrorKind ? (
            <p className="text-sm text-red-600 py-4">
              {ensureErrorKind === 'brokerClient'
                ? t('directChat.ensureErrorBrokerClient')
                : t('directChat.ensureError')}
            </p>
          ) : conversationId && currentUser ? (
            <DirectMessagePanel conversationId={conversationId} currentUser={currentUser} />
          ) : (
            <p className="text-sm text-slate-500">{t('directChat.loginToParticipate')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
